// The top-level Gunsmith Studio screen: assembles the roster/assembly/skin/
// audio panels and the shared live viewport into one Workspace, reusing the
// existing generic Dockview panel-system infrastructure
// (src/studio/editor/panel-system/) exactly as Map Builder/Characters do,
// via its own self-contained PanelDescriptor array rather than editing the
// main editor's buildPanelRegistry in screens-editor.tsx (out of this
// programme's assigned scope, and that file has unrelated work in flight).
//
// Registering this screen into the app's real navigation (one line in
// screens-editor.tsx's buildPanelRegistry, or a dedicated top-level route -
// see leads/studio-editor/status.md for the exact one-line change) is a
// trivial follow-up left for whoever integrates, per this program's own
// "flag it done and verified, the user merges" convention - it does not
// change anything about how this screen itself works.

import { useEffect } from 'react';
import { Workspace } from '../editor/panel-system/Workspace';
import type { PanelDescriptor, PresetDescriptor } from '../editor/panel-system/types';
import { useGunsmithStore } from './store/gunsmithStore';
import { GunsmithToolbar } from './GunsmithToolbar';
import { RosterPanel } from './panels/RosterPanel';
import { AssemblyPanel } from './panels/AssemblyPanel';
import { MaterialPanel } from './panels/MaterialPanel';
import { AudioPanel } from './panels/AudioPanel';
import { WeaponViewport } from './viewport/WeaponViewport';

// Workspace's presetId is a closed union (scene/art/systems/setup/code, the
// main editor's own named workspace presets) - 'art' is reused here purely
// as a valid literal, not because Gunsmith is one of those presets; a
// single-preset Workspace never shows the preset switcher doing anything
// meaningful since there is only one entry.
const GUNSMITH_PRESET_ID = 'art' as const;

const PRESETS: PresetDescriptor[] = [{ id: GUNSMITH_PRESET_ID, label: 'Gunsmith' }];

function ViewportPanel() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const active = weapons.find((w) => w.id === activeWeaponId);
  if (!active) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: 'var(--ss-text-3, #6d747e)', fontSize: 13 }}>
        Select or create a weapon to preview it here.
      </div>
    );
  }
  return <WeaponViewport doc={active} />;
}

const PANELS: PanelDescriptor[] = [
  { id: 'gunsmith.roster', title: 'Roster', presetId: GUNSMITH_PRESET_ID, dimension: 'shared', zone: 'left', render: () => <RosterPanel /> },
  { id: 'gunsmith.viewport', title: 'Preview', presetId: GUNSMITH_PRESET_ID, dimension: 'shared', canvasOwning: true, render: () => <ViewportPanel /> },
  { id: 'gunsmith.assembly', title: 'Assembly', presetId: GUNSMITH_PRESET_ID, dimension: 'shared', zone: 'right', render: () => <AssemblyPanel /> },
  { id: 'gunsmith.material', title: 'Material', presetId: GUNSMITH_PRESET_ID, dimension: 'shared', zone: 'right', render: () => <MaterialPanel /> },
  { id: 'gunsmith.audio', title: 'Audio', presetId: GUNSMITH_PRESET_ID, dimension: 'shared', zone: 'right', render: () => <AudioPanel /> },
];

export interface GunsmithStudioPageProps {
  projectId: string;
}

export function GunsmithStudioPage({ projectId }: GunsmithStudioPageProps) {
  const loadRoster = useGunsmithStore((s) => s.loadRoster);
  const rosterLoaded = useGunsmithStore((s) => s.rosterLoaded);
  const rosterProjectId = useGunsmithStore((s) => s.rosterProjectId);

  useEffect(() => {
    // rosterLoaded alone isn't enough: it's a module-level Zustand flag that
    // outlives this component's own mount/unmount, so switching from one
    // project's Gunsmith Kit to another's (a client-side navigation, not a
    // page reload) would otherwise leave it `true` from the previous
    // project and skip reloading - showing (and letting a save/publish
    // write) one project's weapons under another's id. rosterProjectId
    // catches that mismatch.
    if (!rosterLoaded || rosterProjectId !== projectId) void loadRoster();
  }, [projectId, rosterLoaded, rosterProjectId, loadRoster]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>
      <GunsmithToolbar />
      <Workspace
        // Namespaced, not the real projectId: Workspace persists its layout
        // to `ss-editor-workspace-layout:${projectId}` (layoutStorage.ts),
        // keyed only by this string - reusing the bare projectId would
        // collide with the main Studio editor's own persisted layout for
        // the same project. projectId itself is never forwarded to a panel
        // via this prop (each panel's render() closure already reads real
        // project state from its own hooks), so namespacing it here is safe.
        projectId={`gunsmith:${projectId}`}
        projection="3D"
        panels={PANELS}
        presets={PRESETS}
        activePanelId="gunsmith.assembly"
        // This nests inside one of the outer Studio editor's own panels,
        // which already shows its own "Reset all workspaces" button for the
        // outer preset set - a second one down here was ambiguous (reset
        // which workspace?) and, with only one entry in PRESETS, redundant
        // with "Reset layout" just to its left.
        showResetAllWorkspaces={false}
      />
    </div>
  );
}
